/** Anzeigeformate und Beschriftungen. */

import type { Entry, Person, WerUnterwegs } from '@baba/engine/types';

export const NAME: Record<Person, string> = { michi: 'Michi', otto: 'Otto' };

/** Wer im Auto sitzt - 'keiner' heisst: das Auto fährt, aber ohne uns (R-22). */
export const WER_UNTERWEGS: Record<WerUnterwegs, string> = {
  michi: 'Michi',
  otto: 'Otto',
  beide: 'Beide',
  keiner: 'Niemand von uns',
};

export const ART_TITEL: Record<Entry['type'], string> = {
  trip: 'Ausflug',
  refuel: 'Tanken',
  repair: 'Reparatur',
  settlement: 'Ausgleich',
  fixed_cost: 'Fixkosten',
};

/** Farben nach der Rotation der Prüfplakette - sie tragen Information. */
export const ART_FARBE: Record<Entry['type'], string> = {
  trip: 'var(--ausflug)',
  refuel: 'var(--tanken)',
  repair: 'var(--reparatur)',
  settlement: 'var(--ausgleich)',
  fixed_cost: 'var(--fixkosten)',
};

const euroFormat = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

export function euro(x: number): string {
  return euroFormat.format(x).replace(/\u00A0/g, ' ');
}

export function zahl(x: number, stellen = 1): string {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  }).format(x);
}

export function km(x: number): string {
  return `${zahl(x, x % 1 === 0 ? 0 : 1)} km`;
}

/** '2026-10-03' -> '03.10.2026' */
export function datum(iso: string): string {
  const [j, m, t] = iso.split('-');
  return `${t}.${m}.${j}`;
}

export function heuteIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Eingabe zu Zahl. Auf dem Handy tippt niemand einen Punkt als Dezimaltrenner,
 * aber "1.250,00" aus einer Tankquittung kommt durchaus vor.
 *
 * Regel: Steht ein Komma drin, ist es der Dezimaltrenner und Punkte sind
 * Tausenderzeichen. Ohne Komma gilt ein Punkt als Dezimaltrenner, damit auch
 * "1.5" funktioniert.
 */
export function zuZahl(v: string): number | undefined {
  let text = v.trim().replace(/\s/g, '');
  if (text === '') return undefined;

  if (text.includes(',')) {
    text = text.replace(/\./g, '').replace(',', '.');
  }

  const n = Number(text);
  return Number.isFinite(n) ? n : undefined;
}

export function zuText(v: number | undefined | null, stellen?: number): string {
  if (v == null) return '';
  return stellen == null
    ? String(v).replace('.', ',')
    : v.toFixed(stellen).replace('.', ',');
}

/** Kurzbeschreibung für die Historienzeile. */
export function postenTitel(e: Entry): string {
  if (e.beschreibung) return e.beschreibung;
  switch (e.type) {
    case 'trip':
      if (e.werUnterwegs === 'beide') return 'Michi und Otto unterwegs';
      if (e.werUnterwegs === 'keiner') return 'Ohne uns unterwegs';
      return `${NAME[e.werUnterwegs]} unterwegs`;
    case 'refuel':
      return `${NAME[e.bezahltVon]} hat getankt`;
    case 'repair':
      return `Reparatur, bezahlt von ${NAME[e.bezahltVon]}`;
    case 'settlement':
      return e.richtung === 'michi_an_otto'
        ? 'Michi an Otto'
        : 'Otto an Michi';
    case 'fixed_cost':
      return e.kategorie ?? `Fixkosten, bezahlt von ${NAME[e.bezahltVon]}`;
  }
}
